import { useWrongNetwork } from '@/domain/hooks/useWrongNetwork'
import { CommonDialogProps, DialogConfig } from '@/features/dialogs/common/types'
import { Dialog, DialogContent } from '@/ui/atoms/dialog/Dialog'
import { Address } from 'viem'
import { ECollateralStatusDialogContentContainer } from './ECollateralStatusDialogContentContainer'

export interface ECollateralStatusDialogProps extends CommonDialogProps {
  /** The vault to enable/disable as collateral */
  collateralVault: Address
  /** The underlying asset address */
  collateralAsset: Address
  /** Token symbol for display */
  collateralSymbol: string
  /** Token decimals */
  collateralDecimals: number
  /** Token logo URI */
  collateralLogoURI?: string
  /** Current collateral amount formatted */
  currentCollateralFormatted: number
  /** Collateral price in USD */
  collateralPriceUSD: number
  /** Sub-account ID */
  subAccountId: number
  /** EVC address */
  evcAddress: Address
  /** Action type: 'enable' or 'disable' */
  action: 'enable' | 'disable'
  /** Current debt in USD for health factor calculation */
  currentDebtUSD: number
  /** Total collateral currently backing debt (USD) */
  accountTotalCollateralUSD: number
  /** Liquidation LTV threshold (percentage, e.g., 85 for 85%) */
  liquidationLTV: number
  /** Current LTV from Account Lens (percentage, e.g., 38.57 for 38.57%) */
  currentLTV: number
  /**
   * Dialog mode:
   * - 'collateral': Standard enable/disable collateral flow (same market as controller)
   * - 'controller': Simplified enable/disable for vaults from different markets
   */
  mode?: 'collateral' | 'controller'
}

function ECollateralStatusDialog({
  open,
  setOpen,
  collateralVault,
  collateralAsset,
  collateralSymbol,
  collateralDecimals,
  collateralLogoURI,
  currentCollateralFormatted,
  collateralPriceUSD,
  subAccountId,
  evcAddress,
  action,
  currentDebtUSD,
  accountTotalCollateralUSD,
  liquidationLTV,
  currentLTV,
  mode = 'collateral',
}: ECollateralStatusDialogProps) {
  const isWrongNetwork = useWrongNetwork()
  if (isWrongNetwork) {
    setOpen(false)
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined}>
        <ECollateralStatusDialogContentContainer
          closeDialog={() => setOpen(false)}
          collateralVault={collateralVault}
          collateralAsset={collateralAsset}
          collateralSymbol={collateralSymbol}
          collateralDecimals={collateralDecimals}
          collateralLogoURI={collateralLogoURI}
          currentCollateralFormatted={currentCollateralFormatted}
          collateralPriceUSD={collateralPriceUSD}
          subAccountId={subAccountId}
          evcAddress={evcAddress}
          action={action}
          currentDebtUSD={currentDebtUSD}
          accountTotalCollateralUSD={accountTotalCollateralUSD}
          liquidationLTV={liquidationLTV}
          currentLTV={currentLTV}
          mode={mode}
        />
      </DialogContent>
    </Dialog>
  )
}

export const eCollateralStatusDialogConfig: DialogConfig<ECollateralStatusDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: ECollateralStatusDialog,
}
